import React, { useState } from 'react';
import { LocaleString, getString } from '@nackle/intl-tools';
import classNames from 'classnames';
import { LinkExternal, Spinner, Error } from '@nackle/origami-icons';
import { NackleNotation, Spin } from '../../exports';

const hasPermission = ( app, permission, userPermissions = {} ) => {
    return ( userPermissions[ app ] || [] ).includes( permission );
};

/**
 * Renders marketplace links for merchandise, experiences, and mastery sections.
 *
 * @param {Object} props - Component properties
 * @param {Object} props.permissions - User permissions object
 * @param {Array} props.menuItems - Array of menu items containing page types and logos
 * @param {Function} props.onMarketplaceClick - Callback function when marketplace link is clicked, should return empty object when loading needs to stop or object with `error` boolean if error
 * @param {string} [props.location] - Location of component rendering to determine permissions - ['home', 'deck', 'login']
 * @param {string} [props.target] - Target attribute for links (e.g. '_blank') only needed when using menuItems link properties
 * @returns {React.ReactElement} Marketplace links component
 */
const NackleMarketplaceLinks = ( { permissions, menuItems, onMarketplaceClick, target = '_blank', location } ) => {
    const [ merchLoading, setMerchLoading ] = useState( false );
    const [ experiencesLoading, setExperiencesLoading ] = useState( false );
    const [ masteryLoading, setMasteryLoading ] = useState( false );
    const [ merchErr, setMerchErr ] = useState( false );
    const [ expErr, setExpErr ] = useState( false );
    const [ mastErr, setMastErr ] = useState( false );
    let isMarketLinkEnabled = hasPermission( 'elevate', 'view-marketplace', permissions );
    if ( location === 'login' ) {
        isMarketLinkEnabled = true;
    }

    const marketLinkClasses = classNames(
        'market-link',
        {
            'is-disabled': !isMarketLinkEnabled
        }
    );


    const merch = menuItems.find( item => item.pageType == 'merchandise' );
    const exp = menuItems.find( item => item.pageType == 'experiences' );
    const mastery = menuItems.find( item => item.pageType == 'mastery' );

    /**
     * Handles click events on marketplace links.
     *
     * @param {string} marketplace - Type of marketplace (merchandise/experiences/mastery)
     * @param {Event} e - Click or keydown event object
     */
    const handleClick = async ( marketplace, e ) => {
        if ( e && e.key && e.key !== 'Enter' ) {
            return;
        }
        setMerchErr( false );
        setMastErr( false );
        setExpErr( false );
        if ( !isMarketLinkEnabled ) {
            return;
        }
        if ( isMarketLinkEnabled ) {
            switch ( marketplace ) {
                case 'merchandise':
                    setMerchLoading( true );
                    break;
                case 'experiences':
                    setExperiencesLoading( true );
                    break;
                case 'mastery':
                    setMasteryLoading( true );
                    break;
                default:
                    break;
            }
            if ( typeof onMarketplaceClick === 'function' ) {
                const clicked = await onMarketplaceClick( marketplace, e );
                if ( clicked ) {
                    switch ( marketplace ) {
                        case 'merchandise':
                            setMerchLoading( false );
                            break;
                        case 'experiences':
                            setExperiencesLoading( false );
                            break;
                        case 'mastery':
                            setMasteryLoading( false );
                            break;
                        default:
                            break;
                    }
                }
                if ( clicked.error ) {
                    switch ( marketplace ) {
                        case 'merchandise':
                            setMerchErr( true );
                            break;
                        case 'experiences':
                            setExpErr( true );
                            break;
                        case 'mastery':
                            setMastErr( true );
                            break;
                        default:
                            break;
                    }
                }
            }
        } else {
            e.preventDefault();
        }
    };

    return (
        <div className="origami-redeem-section">
            <ul className="redeem-menus">
                { merch &&
                    <li className="redeem-option">
                        <a tabIndex={ 0 } href={ merch.link ? merch.link : undefined } className={ marketLinkClasses } target={ target } rel="noreferrer" onClick={ ( event ) => handleClick( 'merchandise', event ) } onKeyDown={ ( event ) => handleClick( 'merchandise', event ) }>
                            {
                                merchErr &&
                                <NackleNotation state="error" emphasis icon={ <Error className="error-icon" /> } text={ getString( 'origami.marketplace.error' ) } />
                            }
                            <span className="link-label" >
                                <img src={ merch.colorLogo } alt="" />
                                <div className="rewards-store-description">
                                    <LocaleString code="origami.marketplaceLinks.merchandiseLink" className="link-text" />
                                    <span className="sub-text"><LocaleString code="origami.redeem.merchDescription" /></span>
                                </div>
                                <span className="origami-external-link-icon">
                                    {
                                        !merchLoading &&
                                        <LinkExternal />
                                    }
                                    {
                                        merchLoading &&
                                        <Spin size="mini" spinning={ true } indicator={ <Spinner /> } />
                                    }
                                </span>
                            </span>
                        </a>
                    </li>
                }

                { exp &&
                    <li className="redeem-option">
                        <a tabIndex={ 0 } href={ exp.link ? exp.link : undefined } className={ marketLinkClasses } target={ target } rel="noreferrer" onClick={ ( event ) => handleClick( 'experiences', event ) } onKeyDown={ ( event ) => handleClick( 'experiences', event ) }>
                            {
                                expErr &&
                                <NackleNotation state="error" emphasis icon={ <Error className="error-icon" /> } text={ getString( 'origami.marketplace.error' ) } />
                            }
                            <span className="link-label">
                                <img src={ exp.colorLogo } alt="" />
                                <div className="rewards-store-description">
                                    <LocaleString code="origami.marketplaceLinks.experiencesLink" className="link-text" />
                                    <span className="sub-text"><LocaleString code="origami.redeem.experiencesDescription" /></span>
                                </div>
                                <span className="origami-external-link-icon">
                                    {
                                        !experiencesLoading &&
                                        <LinkExternal />
                                    }
                                    {
                                        experiencesLoading &&
                                        <Spin size="mini" spinning={ true } indicator={ <Spinner /> } />
                                    }
                                </span>
                            </span>
                        </a>
                    </li>
                }
                { mastery &&
                    <li className="redeem-option">
                        <a tabIndex={ 0 } href={ mastery.link ? mastery.link : undefined } className={ marketLinkClasses } target={ target } rel="noreferrer" onClick={ ( event ) => handleClick( 'mastery', event ) } onKeyDown={ ( event ) => handleClick( 'mastery', event ) }>
                            {
                                mastErr &&
                                <NackleNotation state="error" emphasis icon={ <Error className="error-icon" /> } text={ getString( 'origami.marketplace.error' ) } />
                            }
                            <span className="link-label">
                                <img src={ mastery.colorLogo } alt="" />
                                <div className="rewards-store-description">
                                    <LocaleString code="origami.marketplaceLinks.masteryLink" className="link-text" />
                                    <span className="sub-text"><LocaleString code="origami.redeem.masteryDescription" /></span>
                                </div>
                                <span className="origami-external-link-icon">
                                    {
                                        !masteryLoading &&
                                        <LinkExternal />
                                    }
                                    {
                                        masteryLoading &&
                                        <Spin size="mini" spinning={ true } indicator={ <Spinner /> } />
                                    }
                                </span>
                            </span>
                        </a>
                    </li>
                }
            </ul>
        </div>
    );
};
export { NackleMarketplaceLinks };